import { Box } from "@mui/material";
import { DotLegalButton, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalCollapse from "@common/components/dotLegalCollapse/DotLegalCollapse";
import { useTranslation } from "@localization/useTranslation";
import { ValidationError } from "@common/validationError";
import { CounterPartySubContractorViewModel, CounterPartyViewModel } from "../../../Contract.types";
import { SelectableLegalUnitsItem } from "../../../../../processingActivity/ProcessingActivity.types";
import SharingsLegalEntity from "../../../../../processingActivity/sharingOfDataStep/sharingsLegalEntityBox/sharingsLegalEntity";
import SegmentStep from "../../../../../processingActivity/stepSegment/StepSegment";
import { CreateLegalEntityReturnType } from "../../../../../legalEntity/legalEntities/LegalEntities.types";
import CounterParty from "../counterParty/CounterParty";
import { ContractPartyKind } from "../CounterPartiesStep.types";
import { counterPartiesStepStyles } from "../CounterPartiesStep.styles";
import useContractPartyListHooks from "./ContractPartyList.hooks";

export interface IContractPartyList {
    kind: ContractPartyKind;
    contractId: string;
    contractName?: string;
    parties: Array<CounterPartyViewModel>;
    partyOptions?: Array<SelectableLegalUnitsItem>;
    legalEntityData?: Array<SelectableLegalUnitsItem>;
    legalEntityIsLoading: boolean;
    validationErrors: Array<ValidationError>;
    selectedLegalentities: Array<string>;
    expandedId: string;
    onExpandClick: (counterPartyId: string) => void;
    onExpand: (counterPartyId: string) => void;
    onAddParty: (legalEntityId: string) => Promise<void>;
    isAddingParty: boolean;
    onRemoveParty: (counterPartyId: string) => Promise<void>;
    onCounterPartyChange: (id: string, changeFunc: (counterParty: CounterPartyViewModel) => void) => Promise<void>;
    onSubContractorChange: (
        contractPartyId: string,
        subContractorId: string,
        changeFunc: (subContractor: CounterPartySubContractorViewModel) => void
    ) => Promise<void>;
    readOnly: boolean;
}

function ContractPartyList(props: IContractPartyList) {
    const {
        header,
        headerToolTip,
        addPartyText,
        partyPlaceholder,
        showAddNewEntityButton,
        canEdit,
        isExpanded,
        draftId,
        showDraft,
        draftIndex,
        openDraft,
        closeDraft,
        draftPartyOptions,
        draftLegalEntityId,
        onDraftLegalEntityChange,
        clearParty,
    } = useContractPartyListHooks(props);
    const { translateString } = useTranslation();

    const cards = props.parties.map((counterParty) => (
        <CounterParty
            key={counterParty.id}
            counterParty={counterParty}
            isExpanded={isExpanded(counterParty.id)}
            onExpandClick={() => props.onExpandClick(counterParty.id)}
            onRemoveCounterParty={() => props.onRemoveParty(counterParty.id)}
            onClearParty={() => clearParty(counterParty.id)}
            partyOptions={props.partyOptions}
            legalEntityData={props.legalEntityData}
            validationErrors={props.validationErrors.filter((e) => e.field.startsWith(counterParty.id))}
            legalEntityIsLoading={props.legalEntityIsLoading}
            onCounterPartyChange={props.onCounterPartyChange}
            onSubContractorChange={props.onSubContractorChange}
            contractId={props.contractId}
            contractName={props.contractName}
            selectedLegalentities={props.selectedLegalentities}
            hasSubContractors={counterParty.subContractors.length > 0}
            showAddNewEntityButton={showAddNewEntityButton && canEdit}
            partyPlaceholder={partyPlaceholder}
            readonly={!canEdit}
        />
    ));

    if (showDraft) {
        cards.splice(
            draftIndex ?? cards.length,
            0,
            <DotLegalCollapse
                key={draftId}
                sx={{ marginBottom: 2 }}
                isExpanded={isExpanded(draftId)}
                onExpandClick={() => props.onExpandClick(draftId)}
                onRemoveClick={closeDraft}
                removeItemText={translateString("removeContractParty")}
                removeItemStyle="link"
                removeItemSize="small"
                isLoading={props.legalEntityIsLoading}
            >
                <SegmentStep size="fullWidth">
                    <Box sx={counterPartiesStepStyles.partyContent}>
                        <SharingsLegalEntity
                            isLoading={props.legalEntityIsLoading}
                            selectedItem={draftLegalEntityId}
                            placeholder={partyPlaceholder}
                            label={translateString("legalEntity")}
                            options={draftPartyOptions}
                            toolTipText={translateString("addContractPartyTooltip")}
                            onLegalEntityChange={onDraftLegalEntityChange}
                            showAddNewEntityButton={showAddNewEntityButton}
                            createAndNavigateName={props.contractName}
                            createAndNavigateReturnType={CreateLegalEntityReturnType.Contract}
                            disabled={!canEdit || props.isAddingParty}
                        />
                    </Box>
                </SegmentStep>
            </DotLegalCollapse>
        );
    }

    return (
        <>
            <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
                <DotLegalHeader headerStyle={"small"} toolTip={headerToolTip}>
                    {header}
                </DotLegalHeader>
            </Box>

            {cards}

            {canEdit && (
                <Box sx={(theme) => ({ marginTop: theme.spacing(3), display: "flex", justifyContent: "center" })}>
                    <DotLegalButton disabled={showDraft} buttonType="secondary" onClick={openDraft}>
                        {addPartyText}
                    </DotLegalButton>
                </Box>
            )}
        </>
    );
}

export default ContractPartyList;
